import { BIOME_IDS, BIOMES } from '../biomes.js';
import { GRASS, terrainOf } from '../terrains.js';
import { cheapestPath } from './path-cost.js';
import { clearBrush } from './world-terrain.js';
/** A trail longer than this is a hike nobody follows: the target is refused. */
export const TRAIL_MAX = 150;
/** The walkable ground of each biome: earth meets any one of them, never two at once. */
const GROUNDS = new Set(BIOME_IDS.map((id) => BIOMES[id].ground));
/**
 * Trails of packed earth from the roads to a spot, by the cheapest way round
 * whatever stands: a trail leaves the network wherever that is closest, and
 * the next trail may follow it. Shared by the places of interest and the
 * lairs, so a lair's trail may branch off a place's footpath.
 */
export class TrailCarver {
    fields;
    occupancy;
    root;
    cost;
    /** The earth one can walk from the capital's square: the only place a trail may start. */
    roads = [];
    /** Steps of open ground from that earth to each tile; 0xffff where none leads. */
    reach;
    constructor(fields, occupancy, root) {
        this.fields = fields;
        this.occupancy = occupancy;
        this.root = root;
        const { size } = fields;
        this.cost = new Float32Array(size * size);
        this.reach = new Uint16Array(size * size);
        for (let i = 0; i < size * size; i += 1)
            this.refresh(i);
        this.connect();
    }
    /**
     * Which earth is joined to the capital: a place's clearing is earth too,
     * but a trail that ended there would lead nowhere until the place's own
     * footpath is dug. Swept again after every trail, which may have joined
     * some.
     */
    connect() {
        const { fields } = this;
        const { size } = fields;
        const seen = new Uint8Array(size * size);
        const start = fields.index(this.root.x, this.root.y);
        const queue = [start];
        seen[start] = 1;
        for (const i of queue) {
            const x = i % size;
            const y = (i - x) / size;
            for (const [dx, dy] of [
                [1, 0],
                [-1, 0],
                [0, 1],
                [0, -1],
            ]) {
                if (!fields.inside(x + dx, y + dy))
                    continue;
                const j = fields.index(x + dx, y + dy);
                const char = fields.terrain[j];
                if ((char === 'd' || char === 'b') && seen[j] !== 1) {
                    seen[j] = 1;
                    queue.push(j);
                }
            }
        }
        this.roads = queue;
        // And how far every tile is from that earth, over passable ground.
        this.reach.fill(0xffff);
        const front = [...queue];
        for (const i of front)
            this.reach[i] = 0;
        for (const i of front) {
            const x = i % size;
            const y = (i - x) / size;
            const next = (this.reach[i] ?? 0) + 1;
            for (const [dx, dy] of [
                [1, 0],
                [-1, 0],
                [0, 1],
                [0, -1],
            ]) {
                if (!fields.inside(x + dx, y + dy))
                    continue;
                const j = fields.index(x + dx, y + dy);
                if (this.cost[j] !== Infinity && (this.reach[j] ?? 0) > next) {
                    this.reach[j] = next;
                    front.push(j);
                }
            }
        }
    }
    refresh(i) {
        const { fields } = this;
        const char = fields.terrain[i] ?? '.';
        const earth = char === 'd' || char === 'b';
        // Locked tiles (a cliff's ring, stairs) are never repainted, so a trail
        // through them would have a gap: it goes round, unless a road already
        // runs there, through a gorge.
        const open = terrainOf(char).walkable &&
            this.occupancy.blocked[i] !== 1 &&
            (fields.locked[i] !== 1 || earth);
        if (!open) {
            this.cost[i] = Infinity;
            return;
        }
        if (earth) {
            this.cost[i] = 0.5;
            return;
        }
        // Earth meets no water and no rock (no tileset joins them), and the
        // repair of a knot reaches a tile beyond it: within two tiles of them the
        // trail would be wiped, so that ground is dear, as a shore is to a road.
        // Thicket and bog that close are cleared instead when the trail is laid,
        // as along the roads, so they only cost a little.
        const beside = this.beside(i);
        if (beside === 'wet')
            this.cost[i] = 40;
        else if (beside === 'brush')
            this.cost[i] = 6;
        // Where two grounds meet, earth would make a knot of three that the
        // repair unties by wiping the earth: a trail crosses from one biome to
        // the next only where a road already does.
        else
            this.cost[i] = this.onBorder(i) ? 30 : 1.5;
    }
    /** Whether another biome's ground lies within two tiles. */
    onBorder(i) {
        const { fields } = this;
        const { size } = fields;
        const x = i % size;
        const y = (i - x) / size;
        const own = fields.terrain[i] ?? GRASS;
        for (let dy = -2; dy <= 2; dy += 1) {
            for (let dx = -2; dx <= 2; dx += 1) {
                if (!fields.inside(x + dx, y + dy))
                    continue;
                const char = fields.terrain[fields.index(x + dx, y + dy)] ?? GRASS;
                if (char !== own && GROUNDS.has(char))
                    return true;
            }
        }
        return false;
    }
    /** What stands within two tiles: water, ice or rock; thicket or bog; or nothing of note. */
    beside(i) {
        const { fields } = this;
        const { size } = fields;
        const x = i % size;
        const y = (i - x) / size;
        let brush = false;
        for (let dy = -2; dy <= 2; dy += 1) {
            for (let dx = -2; dx <= 2; dx += 1) {
                if ((dx === 0 && dy === 0) || !fields.inside(x + dx, y + dy))
                    continue;
                const char = fields.terrain[fields.index(x + dx, y + dy)] ?? '.';
                if (char === 'w' || char === 'i' || char === 'r')
                    return 'wet';
                if (char === 'f' || char === 'm')
                    brush = true;
            }
        }
        return brush ? 'brush' : null;
    }
    /** Thicket and bog within two tiles of a tile of earth become the biome's ground: nothing paints the earth shut. */
    clearBrush(x, y) {
        clearBrush(this.fields, x, y);
    }
    /**
     * Carve a trail to `target` from the nearest road outside `exclude` tiles
     * of it (a place's own clearing is dirt too). `forbidden` tiles are not
     * crossed, whatever they are: a lair's trail may not slip round the
     * fortress and arrive behind it. Returns the trail, road end first, or
     * null when none of reasonable length exists.
     */
    carve(target, exclude, maxLength = TRAIL_MAX, forbidden = new Set()) {
        const { fields } = this;
        const { size } = fields;
        // What was built since the last trail blocks the way.
        for (let i = 0; i < size * size; i += 1) {
            if (this.occupancy.blocked[i] === 1 && this.cost[i] !== Infinity)
                this.cost[i] = Infinity;
        }
        const cost = forbidden.size === 0 ? this.cost : this.cost.slice();
        for (const i of forbidden)
            cost[i] = Infinity;
        const sources = this.roads.filter((i) => {
            const x = i % size;
            return Math.hypot(x - target.x, (i - x) / size - target.y) > exclude && cost[i] !== Infinity;
        });
        if (sources.length === 0)
            return null;
        const path = cheapestPath({ width: size, height: size, cost }, sources, fields.index(target.x, target.y));
        if (path === null || path.length > maxLength)
            return null;
        const touched = new Set();
        for (const i of path) {
            if (fields.terrain[i] === 'b' || this.occupancy.blocked[i] === 1 || fields.locked[i] === 1)
                continue;
            if (fields.terrain[i] !== 'd') {
                const x = i % size;
                const y = (i - x) / size;
                if (this.beside(i) === 'brush')
                    this.clearBrush(x, y);
                fields.terrain[i] = 'd';
                for (let dy = -4; dy <= 4; dy += 1) {
                    for (let dx = -4; dx <= 4; dx += 1) {
                        if (fields.inside(x + dx, y + dy))
                            touched.add(fields.index(x + dx, y + dy));
                    }
                }
            }
        }
        for (const i of touched)
            this.refresh(i);
        this.connect();
        return path;
    }
}
//# sourceMappingURL=world-trails.js.map